import React, { useState, useEffect, useMemo } from 'react';
import { Navigation } from '@/components/layout/Navigation';
import { Footer } from '@/components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Store, 
  MapPin, 
  Package,
  Search,
  QrCode,
  ExternalLink,
  Loader2,
  Navigation2,
  ShoppingBag
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;

// Debounce hook for smooth search-while-typing
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    
    return () => clearTimeout(handler);
  }, [value, delay]);
  
  return debouncedValue;
};

const BrowseStoresPage = () => {
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltering, setIsFiltering] = useState(false);
  
  // Debounce search for smoother UX
  const debouncedSearch = useDebounce(searchQuery, 200);

  useEffect(() => {
    fetchAllStores();
  }, []);

  useEffect(() => {
    setIsFiltering(true);
    if (debouncedSearch.trim() === '') {
      setFilteredStores(stores);
    } else {
      const query = debouncedSearch.toLowerCase();
      setFilteredStores(stores.filter(store => 
        store.name?.toLowerCase().includes(query) ||
        store.city?.toLowerCase().includes(query) ||
        store.postcode?.toLowerCase().includes(query) ||
        store.address?.toLowerCase().includes(query)
      ));
    }
    setIsFiltering(false);
  }, [debouncedSearch, stores]);

  const fetchAllStores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/stores/all`);
      setStores(response.data.stores || []);
      setFilteredStores(response.data.stores || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
      toast.error('Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  // Group stores by city - memoized for performance
  const storesByCity = useMemo(() => {
    return filteredStores.reduce((acc, store) => {
      const city = store.city || 'Other';
      if (!acc[city]) acc[city] = [];
      acc[city].push(store);
      return acc;
    }, {});
  }, [filteredStores]);

  return (
    <div className="min-h-screen bg-background" data-testid="browse-stores-page">
      <Navigation />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5DBDB3] to-[#4AA89E] flex items-center justify-center mx-auto mb-4 shadow-lg">
            <ShoppingBag className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Find Allergen-Safe Stores</h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Browse stores in the SEEN network. Scan their QR code to instantly see which products are safe for your allergies.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Start typing to filter stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9"
              data-testid="store-search-input"
            />
            {isFiltering && (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#5DBDB3]" />
            )}
          </div>
          {searchQuery && filteredStores.length === 0 && !loading && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              No stores match "{searchQuery}"
            </p>
          )}
        </div>

        {/* Stats */}
        <div className="flex justify-center gap-6 mb-8">
          <div className="text-center">
            <p className="text-2xl font-bold text-[#5DBDB3]">{filteredStores.length}</p>
            <p className="text-sm text-muted-foreground">Stores</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#5DBDB3]">{Object.keys(storesByCity).length}</p>
            <p className="text-sm text-muted-foreground">Cities</p>
          </div>
        </div>

        {/* Stores List */}
        {loading ? (
          <div className="flex items-center justify-center py-12" data-testid="stores-loading">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : filteredStores.length === 0 ? (
          <Card data-testid="no-stores-card">
            <CardContent className="p-12 text-center">
              <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {searchQuery ? 'No Stores Found' : 'No Stores Available Yet'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery 
                  ? 'Try a different search term or browse all stores'
                  : 'Check back soon as more stores join the SEEN network'}
              </p>
              {searchQuery && (
                <Button variant="outline" onClick={() => setSearchQuery('')}>
                  Clear Search
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8" data-testid="stores-by-city">
            {Object.entries(storesByCity).sort(([a], [b]) => a.localeCompare(b)).map(([city, cityStores]) => (
              <div key={city}>
                <div className="flex items-center gap-2 mb-4">
                  <Navigation2 className="w-4 h-4 text-[#5DBDB3]" />
                  <h2 className="text-lg font-semibold">{city}</h2>
                  <Badge variant="secondary" className="ml-2">{cityStores.length}</Badge>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {cityStores.map((store) => (
                    <Card 
                      key={store.id} 
                      className="hover:shadow-lg transition-all hover:border-[#5DBDB3]/50 group"
                      data-testid={`store-card-${store.id}`}
                    >
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-start justify-between gap-2">
                          <span className="group-hover:text-[#5DBDB3] transition-colors">
                            {store.name}
                          </span>
                          <Badge 
                            variant="outline" 
                            className="shrink-0 text-[10px] bg-[#5DBDB3]/10 text-[#5DBDB3] border-[#5DBDB3]/30"
                          >
                            <Package className="w-2.5 h-2.5 mr-1" />
                            {store.inventory_count || 0}
                          </Badge>
                        </CardTitle>
                        <CardDescription className="flex items-start gap-1 text-xs">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                          <span>{store.address}, {store.postcode}</span>
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-2">
                        {store.description && (
                          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                            {store.description}
                          </p>
                        )}
                        
                        <div className="flex gap-2">
                          <Button 
                            asChild 
                            size="sm" 
                            className="flex-1 bg-[#5DBDB3] hover:bg-[#4AA89E] h-8 text-xs"
                          >
                            <Link to={`/store/${store.id}/scan`}>
                              <QrCode className="w-3 h-3 mr-1.5" />
                              Scan Store
                            </Link>
                          </Button>
                          <Button 
                            asChild 
                            size="sm" 
                            variant="outline" 
                            className="h-8 text-xs"
                          >
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${store.name} ${store.address} ${store.city} ${store.postcode}`)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Map
                            </a>
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA for store owners */}
        <Card className="mt-12 border-[#5DBDB3]/30 bg-gradient-to-br from-[#5DBDB3]/5 to-transparent">
          <CardContent className="p-8 text-center">
            <Store className="w-10 h-10 text-[#5DBDB3] mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-2">Own a Store?</h3>
            <p className="text-muted-foreground text-sm mb-4 max-w-md mx-auto">
              Join the SEEN network and help your customers find allergen-safe products. 
              Create QR codes for your store and manage your inventory.
            </p>
            <Button asChild className="bg-[#5DBDB3] hover:bg-[#4AA89E]">
              <Link to="/subscription">Get Started</Link>
            </Button>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default BrowseStoresPage;
